if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.dispatchAddOrIncrement(formData, config)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // If the variant being added is already in the cart with identical
      // properties / selling plan, increment that line via /cart/change.js
      // instead of POSTing /cart/add.js. This prevents duplicate line items
      // from appearing in the cart drawer for the same product.
      async dispatchAddOrIncrement(formData, config) {
        try {
          const variantId = formData.get('id');
          const requestedQty = parseInt(formData.get('quantity') || '1', 10) || 1;
          if (!variantId) throw new Error('missing variant id');

          const formProperties = {};
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) formProperties[match[1]] = value;
          }
          const formSellingPlan = formData.get('selling_plan') || '';

          const cartRes = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          if (!cartRes.ok) throw new Error('cart fetch failed');
          const cart = await cartRes.json();

          const existingLine = (cart.items || []).find((item) =>
            this.lineMatches(item, variantId, formProperties, formSellingPlan)
          );

          if (!existingLine) {
            const res = await fetch(`${routes.cart_add_url}`, config);
            return await res.json();
          }

          return await this.incrementExistingLine(existingLine, requestedQty, formData);
        } catch (e) {
          // On any unexpected error, fall back to the original /cart/add.js flow
          // so the user can still add the product.
          const res = await fetch(`${routes.cart_add_url}`, config);
          return await res.json();
        }
      }

      lineMatches(item, variantId, formProperties, formSellingPlan) {
        if (String(item.variant_id) !== String(variantId)) return false;

        const itemSellingPlan = item.selling_plan_allocation
          ? String(item.selling_plan_allocation.selling_plan?.id || '')
          : '';
        if (itemSellingPlan !== String(formSellingPlan || '')) return false;

        const itemProperties = item.properties || {};
        const itemKeys = Object.keys(itemProperties).filter((k) => itemProperties[k] !== null && itemProperties[k] !== '');
        const formKeys = Object.keys(formProperties).filter((k) => formProperties[k] !== null && formProperties[k] !== '');
        if (itemKeys.length !== formKeys.length) return false;

        for (const key of itemKeys) {
          if (!(key in formProperties)) return false;
          if (String(itemProperties[key]) !== String(formProperties[key])) return false;
        }
        return true;
      }

      async incrementExistingLine(existingLine, addedQty, formData) {
        const newQty = (existingLine.quantity || 0) + addedQty;
        const sections = formData.get('sections') || '';
        const body = {
          id: existingLine.key,
          quantity: newQty,
          sections,
          sections_url: window.location.pathname,
        };

        const changeUrl = (routes && routes.cart_change_url) || '/cart/change.js';
        const res = await fetch(`${changeUrl}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });
        const response = await res.json();

        // /cart/change.js returns the cart, not the changed line item.
        // Expose the updated line item's key / variant id at the top level so
        // cart-notification (which reads parsedState.key) and cart-drawer
        // (which reads parsedState.id) can render correctly.
        if (response && !response.status) {
          const updated = (response.items || []).find((it) => it.key === existingLine.key);
          if (updated) {
            response.key = updated.key;
            response.id = updated.variant_id;
            response.variant_id = updated.variant_id;
            response.quantity = updated.quantity;
          }
        }
        return response;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
